import React from 'react';
import { Mic, MicOff, Loader2, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FOREST_GREEN, MID_GRAY, WHITE } from '@/styles/tokens';
import type { VoiceState } from '@/services/voice/types';

interface VoiceMicButtonProps {
  voiceState: VoiceState;
  onPress: () => void;
  onStopSpeaking?: () => void;
  disabled?: boolean;
  error?: string | null;
  /** Render as a small ghost icon button (no background when idle) */
  compact?: boolean;
}

const stateConfig: Record<VoiceState, {
  icon: React.ReactNode;
  bg: string;
  pulse?: boolean;
  label: string;
}> = {
  idle: {
    icon: <Mic className="w-4 h-4" />,
    bg: MID_GRAY.DEFAULT,
    label: 'Start voice input',
  },
  'loading-models': {
    icon: <Loader2 className="w-4 h-4 animate-spin" />,
    bg: MID_GRAY.lighter,
    label: 'Loading voice models...',
  },
  listening: {
    icon: <Mic className="w-4 h-4" />,
    bg: FOREST_GREEN.DEFAULT,
    pulse: true,
    label: 'Listening... speak now (click to cancel)',
  },
  'speech-detected': {
    icon: <Mic className="w-4 h-4" />,
    bg: FOREST_GREEN.light,
    pulse: true,
    label: 'Recording... will auto-stop when you pause',
  },
  verifying: {
    icon: <Loader2 className="w-4 h-4 animate-spin" />,
    bg: FOREST_GREEN.DEFAULT,
    label: 'Verifying speaker...',
  },
  transcribing: {
    icon: <Loader2 className="w-4 h-4 animate-spin" />,
    bg: FOREST_GREEN.DEFAULT,
    label: 'Transcribing...',
  },
  processing: {
    icon: <Loader2 className="w-4 h-4 animate-spin" />,
    bg: FOREST_GREEN.DEFAULT,
    label: 'Processing command...',
  },
  speaking: {
    icon: <Volume2 className="w-4 h-4" />,
    bg: FOREST_GREEN.DEFAULT,
    label: 'Speaking response... click to stop',
  },
  error: {
    icon: <MicOff className="w-4 h-4" />,
    bg: '#ef4444',
    label: 'Voice error',
  },
};

export function VoiceMicButton({
  voiceState,
  onPress,
  onStopSpeaking,
  disabled,
  error,
  compact,
}: VoiceMicButtonProps) {
  const config = stateConfig[voiceState];
  const isActive = voiceState === 'listening' || voiceState === 'speech-detected';
  const isBusy = voiceState === 'loading-models';
  const isIdle = voiceState === 'idle';

  const handleClick = () => {
    if (voiceState === 'speaking' && onStopSpeaking) {
      onStopSpeaking();
    } else {
      onPress();
    }
  };

  if (compact) {
    return (
      <>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={handleClick}
          disabled={disabled || isBusy}
          aria-label={config.label}
          title={error || config.label}
          data-testid="voice-mic-button"
          style={{
            color: isIdle ? MID_GRAY.DEFAULT : config.bg,
            animation: config.pulse ? 'voice-pulse-compact 1.5s infinite' : undefined,
          }}
        >
          {config.icon}
        </Button>
        <style>{`
          @keyframes voice-pulse-compact {
            0%, 100% { box-shadow: 0 0 0 0 ${FOREST_GREEN.DEFAULT}44; }
            50% { box-shadow: 0 0 0 6px ${FOREST_GREEN.DEFAULT}00; }
          }
        `}</style>
      </>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <Button
        type="button"
        onClick={handleClick}
        disabled={disabled || isBusy}
        size="sm"
        aria-label={config.label}
        title={error || config.label}
        data-testid="voice-mic-button"
        style={{
          backgroundColor: config.bg,
          color: WHITE,
          minHeight: '44px',
          minWidth: '44px',
          borderRadius: '6px',
          border: 'none',
          transition: 'background-color 150ms ease-in-out',
          animation: config.pulse ? 'voice-pulse 1.5s infinite' : undefined,
        }}
      >
        {config.icon}
      </Button>

      {/* Pulse animation keyframes injected once */}
      <style>{`
        @keyframes voice-pulse {
          0%, 100% { box-shadow: 0 0 0 0 ${FOREST_GREEN.DEFAULT}66; }
          50% { box-shadow: 0 0 0 8px ${FOREST_GREEN.DEFAULT}00; }
        }
      `}</style>
    </div>
  );
}
